import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BadgeInfo,
  ChevronLeft,
  ClipboardList,
  HeartPulse,
  RotateCcw,
  Share2,
  Stethoscope,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { cpotIndicators, flagLabels, functionalAssessment, painScaleRows, routeByPath, visibleRecommendations, workflowRoutes } from "../data/guideline";
import type { CpotIndicator, RecommendationStrength, WorkflowRoute } from "../data/types";
import {
  buildUrl,
  defaultWorkflowState,
  mergeWorkflowState,
  painBand,
  parseWorkflowState,
  supportedFlags,
  type EgfrBand,
  type OpioidChoice,
  type WorkflowFlag,
  type WorkflowState,
} from "../lib/state";

type NavPatch = Partial<WorkflowState>;
type WorkflowStageId = Exclude<WorkflowState["stage"], "unknown">;

const workflowNav = workflowRoutes.filter(
  (route): route is WorkflowRoute & { id: WorkflowStageId } => route.group === "workflow",
);
const toolsNav = workflowRoutes.filter((route) => route.group === "tool" && route.id !== "tools");
const safetyAcknowledgementKey = "cardiac-analgesia-guide:safety-acknowledged";

function getCurrentRoute(pathname: string): WorkflowRoute {
  return routeByPath.get(pathname) ?? routeByPath.get("/start")!;
}

function useUrlWorkflow() {
  const [locationSnapshot, setLocationSnapshot] = useState(() => ({
    pathname: window.location.pathname === "/" ? "/start" : window.location.pathname,
    search: window.location.search,
  }));

  useEffect(() => {
    if (window.location.pathname === "/") {
      window.history.replaceState({}, "", "/start");
      setLocationSnapshot({ pathname: "/start", search: "" });
    }

    const onPopState = () => {
      setLocationSnapshot({
        pathname: window.location.pathname === "/" ? "/start" : window.location.pathname,
        search: window.location.search,
      });
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const state = useMemo(() => parseWorkflowState(locationSnapshot.search), [locationSnapshot.search]);
  const route = getCurrentRoute(locationSnapshot.pathname);

  const navigate = useCallback(
    (path: string, patch: NavPatch = {}, mode: "push" | "replace" = "push") => {
      const nextState = mergeWorkflowState(parseWorkflowState(window.location.search), patch);
      const url = buildUrl(path, { ...nextState, route: path });
      if (mode === "replace") {
        window.history.replaceState({}, "", url);
      } else {
        window.history.pushState({}, "", url);
      }
      setLocationSnapshot({ pathname: path, search: window.location.search });
    },
    [],
  );

  const updateState = useCallback(
    (patch: NavPatch) => {
      const nextState = mergeWorkflowState(parseWorkflowState(window.location.search), patch);
      const path = window.location.pathname === "/" ? "/start" : window.location.pathname;
      const url = buildUrl(path, nextState);
      window.history.replaceState({}, "", url);
      setLocationSnapshot({ pathname: path, search: window.location.search });
    },
    [],
  );

  const reset = useCallback(() => {
    window.history.pushState({}, "", "/start");
    setLocationSnapshot({ pathname: "/start", search: "" });
  }, []);

  return { route, state, navigate, updateState, reset };
}

export function App() {
  const { route, state, navigate, updateState, reset } = useUrlWorkflow();
  const [showSafetyModal, setShowSafetyModal] = useState(() => localStorage.getItem(safetyAcknowledgementKey) !== "true");

  const acknowledgeSafety = useCallback(() => {
    localStorage.setItem(safetyAcknowledgementKey, "true");
    setShowSafetyModal(false);
  }, []);

  return (
    <div className="app-shell">
      <header className="topbar">
        <button className="brand-button" onClick={() => navigate("/start")} aria-label="Go to start">
          <HeartPulse size={22} aria-hidden />
          <span>
            <strong>Cardiac Analgesia</strong>
            <small>Workflow guide</small>
          </span>
        </button>
        <button className="icon-text-button secondary" onClick={reset}>
          <RotateCcw size={18} aria-hidden />
          <span>Start over</span>
        </button>
      </header>

      <main className="screen">
        {showSafetyModal && <SafetyModal onAcknowledge={acknowledgeSafety} />}
        {route.id === "start" && <StartScreen navigate={navigate} />}
        {route.id === "setup" && <SetupScreen state={state} updateState={updateState} navigate={navigate} />}
        {route.id === "tools" && <ToolsOverview state={state} navigate={navigate} />}
        {route.group === "workflow" && <WorkflowScreen route={route} state={state} navigate={navigate} updateState={updateState} />}
        {route.id === "pain-score" && <PainScoreTool state={state} updateState={updateState} navigate={navigate} />}
        {route.id === "function" && <FunctionTool state={state} updateState={updateState} navigate={navigate} />}
        {route.id === "cpot" && <CpotTool state={state} updateState={updateState} navigate={navigate} />}
      </main>

      <nav className="bottom-nav" aria-label="Workflow navigation">
        <button className={route.id === "setup" ? "active" : ""} onClick={() => navigate("/setup")}>
          <ClipboardList size={18} aria-hidden />
          <span>Details</span>
        </button>
        <button className={route.group === "workflow" ? "active" : ""} onClick={() => navigate(pathForStage(state.stage))}>
          <Stethoscope size={18} aria-hidden />
          <span>Pathway</span>
        </button>
        <button className={route.group === "tool" ? "active" : ""} onClick={() => navigate("/tools")}>
          <Activity size={18} aria-hidden />
          <span>Tools</span>
        </button>
      </nav>
    </div>
  );
}

function SafetyModal({ onAcknowledge }: { onAcknowledge: () => void }) {
  return (
    <div className="modal-backdrop" role="presentation">
      <section className="safety-modal" role="dialog" aria-modal="true" aria-labelledby="safety-title">
        <AlertTriangle size={24} aria-hidden />
        <div>
          <h2 id="safety-title">Before using this guide</h2>
          <p>
            Use this alongside the approved local guideline and clinician judgement. Do not enter patient names, identifiers, dates of birth,
            or free-text clinical notes.
          </p>
        </div>
        <button className="icon-text-button primary" onClick={onAcknowledge}>
          <ArrowRight size={18} aria-hidden />
          <span>I understand</span>
        </button>
      </section>
    </div>
  );
}

function StartScreen({ navigate }: { navigate: (path: string, patch?: NavPatch) => void }) {
  return (
    <section className="stack">
      <div className="page-heading">
        <p className="eyebrow">Barts Health guideline support</p>
        <h1>Multimodal peri-operative analgesia for routine cardiac surgery</h1>
        <p>Choose the phase. Add patient details when the pathway needs them.</p>
      </div>

      <div className="action-grid">
        <button className="primary-action" onClick={() => navigate("/setup")}>
          <ClipboardList size={22} aria-hidden />
          <span>Add patient details</span>
          <ArrowRight size={18} aria-hidden />
        </button>
        {workflowNav.map((route) => (
          <button key={route.id} className="route-tile" onClick={() => navigate(route.path, { stage: route.id })}>
            <span>{route.label}</span>
            <small>{route.summary}</small>
          </button>
        ))}
      </div>
    </section>
  );
}

function SetupScreen({
  state,
  updateState,
  navigate,
}: {
  state: WorkflowState;
  updateState: (patch: NavPatch) => void;
  navigate: (path: string, patch?: NavPatch) => void;
}) {
  return (
    <section className="stack">
      <div className="page-heading compact">
        <p className="eyebrow">Patient details</p>
        <h1>Tailor the pathway</h1>
      </div>

      <Fieldset legend="Age band">
        <SegmentedControl
          value={state.age}
          options={[
            ["unknown", "Unknown"],
            ["under75", "< 75"],
            ["over75", "75+"],
          ]}
          onChange={(age) => updateState({ age: age as WorkflowState["age"] })}
        />
      </Fieldset>

      <Fieldset legend="eGFR band">
        <SegmentedControl
          value={state.egfr}
          options={[
            ["unknown", "Unknown"],
            ["gt40", "> 40"],
            ["30to40", "30-40"],
            ["20to30", "20-30"],
            ["lt20", "< 20 / ESRF"],
          ]}
          onChange={(egfr) => updateState({ egfr: egfr as EgfrBand })}
        />
      </Fieldset>

      <Fieldset legend="Patient state">
        <SegmentedControl
          value={state.sedation}
          options={[
            ["unknown", "Unknown"],
            ["sedated", "Sedated"],
            ["extubated", "Extubated"],
          ]}
          onChange={(sedation) => updateState({ sedation: sedation as WorkflowState["sedation"] })}
        />
      </Fieldset>

      <Fieldset legend="Intra-operative opioid">
        <SegmentedControl
          value={state.opioid}
          options={[
            ["unknown", "Unknown"],
            ["morphine", "Morphine"],
            ["oxycodone", "Oxycodone"],
            ["fentanyl", "Fentanyl"],
          ]}
          onChange={(opioid) => updateState({ opioid: opioid as OpioidChoice })}
        />
      </Fieldset>

      <Fieldset legend="Clinical features">
        <div className="check-grid">
          {supportedFlags.map((flag) => (
            <label key={flag} className="check-option">
              <input
                type="checkbox"
                checked={state.flags.includes(flag)}
                onChange={(event) => {
                  const nextFlags = event.currentTarget.checked
                    ? [...state.flags, flag]
                    : state.flags.filter((item) => item !== flag);
                  updateState({ flags: nextFlags });
                }}
              />
              <span>{flagLabels[flag]}</span>
            </label>
          ))}
        </div>
      </Fieldset>

      <div className="action-row">
        <button className="icon-text-button primary" onClick={() => navigate(pathForStage(state.stage), { stage: state.stage })}>
          <ArrowRight size={18} aria-hidden />
          <span>Continue</span>
        </button>
      </div>
    </section>
  );
}

function WorkflowScreen({
  route,
  state,
  navigate,
  updateState,
}: {
  route: WorkflowRoute;
  state: WorkflowState;
  navigate: (path: string, patch?: NavPatch) => void;
  updateState: (patch: NavPatch) => void;
}) {
  const visible = visibleRecommendations(route.id, state);
  const stagePatch = route.id === state.stage ? {} : { stage: route.id as WorkflowState["stage"] };

  useEffect(() => {
    if (route.group === "workflow" && state.stage !== route.id) {
      updateState({ stage: route.id as WorkflowState["stage"] });
    }
  }, [route.group, route.id, state.stage, updateState]);

  return (
    <section className="stack">
      <div className="page-heading compact">
        <p className="eyebrow">Phase</p>
        <h1>{route.label}</h1>
        <p>{route.summary}</p>
      </div>

      <PatientDetailsSummary navigate={navigate} />

      <div className="route-strip" aria-label="Workflow steps">
        {workflowNav.map((navRoute) => (
          <button
            key={navRoute.id}
            className={navRoute.id === route.id ? "active" : ""}
            onClick={() => navigate(navRoute.path, { stage: navRoute.id as WorkflowState["stage"] })}
          >
            {navRoute.shortLabel}
          </button>
        ))}
      </div>

      <section className="recommendation-list" aria-label={`${route.label} recommendations`}>
        {visible.map((recommendation) => (
          <RecommendationCard key={recommendation.id} recommendation={recommendation} />
        ))}
      </section>

      <ToolLinks navigate={navigate} state={state} />

      <div className="action-row">
        <button className="icon-text-button secondary" onClick={() => navigate("/setup", stagePatch)}>
          <ChevronLeft size={18} aria-hidden />
          <span>Adjust patient details</span>
        </button>
      </div>
    </section>
  );
}

function RecommendationCard({ recommendation }: { recommendation: ReturnType<typeof visibleRecommendations>[number] }) {
  return (
    <article className={`recommendation-card ${recommendation.strength}`}>
      <div className="card-heading">
        <span className="strength-dot" aria-hidden />
        <div>
          <p className="card-kicker">{strengthLabel(recommendation.strength)}</p>
          <h2>{recommendation.title}</h2>
        </div>
      </div>
      {recommendation.missingContext.length > 0 && (
        <p className="context-warning">
          Add patient details for {recommendation.missingContext.join(", ")} to narrow this advice.
        </p>
      )}
      <p className="summary">{recommendation.summary}</p>
      {recommendation.details.length > 0 && (
        <ul>
          {recommendation.details.map((detail) => (
            <li key={detail}>{detail}</li>
          ))}
        </ul>
      )}
      {recommendation.cautions && (
        <div className="caution-block">
          <AlertTriangle size={16} aria-hidden />
          <p>{recommendation.cautions.join(" ")}</p>
        </div>
      )}
    </article>
  );
}

function PatientDetailsSummary({ navigate }: { navigate: (path: string, patch?: NavPatch) => void }) {
  return (
    <section className="context-summary" aria-label="Patient details">
      <div>
        <strong>Patient details</strong>
        <p>Saved in the URL for this workflow. Edit them to refine ambiguous advice.</p>
      </div>
      <button className="icon-button" aria-label="Share patient details in URL" title="Patient details are encoded in the URL">
        <Share2 size={18} aria-hidden />
      </button>
      <button className="icon-text-button secondary" onClick={() => navigate("/setup")}>
        <ClipboardList size={18} aria-hidden />
        <span>Edit</span>
      </button>
    </section>
  );
}

function ToolsOverview({ state, navigate }: { state: WorkflowState; navigate: (path: string, patch?: NavPatch) => void }) {
  return (
    <section className="stack">
      <div className="page-heading compact">
        <p className="eyebrow">Tools</p>
        <h1>Scoring and assessment</h1>
        <p>Use the tools directly, or open them from the relevant phase.</p>
      </div>
      <div className="action-grid">
        {toolsNav.map((route) => (
          <button key={route.id} className="route-tile" onClick={() => navigate(route.path, { ...state, route: route.path })}>
            <span>{route.label}</span>
            <small>{route.summary}</small>
          </button>
        ))}
      </div>
      <ReturnToPathway navigate={navigate} state={state} />
    </section>
  );
}

function ToolLinks({ navigate, state }: { navigate: (path: string, patch?: NavPatch) => void; state: WorkflowState }) {
  return (
    <section className="tool-panel">
      <div>
        <p className="card-kicker">Tools</p>
        <h2>Scoring and function checks</h2>
      </div>
      <div className="tool-link-grid">
        {toolsNav.map((route) => (
          <button key={route.id} onClick={() => navigate(route.path, { ...state, route: route.path })}>
            <BadgeInfo size={18} aria-hidden />
            <span>{route.label}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

function PainScoreTool({
  state,
  updateState,
  navigate,
}: {
  state: WorkflowState;
  updateState: (patch: NavPatch) => void;
  navigate: (path: string, patch?: NavPatch) => void;
}) {
  const pain = state.pain ?? 0;
  return (
    <section className="stack">
      <div className="page-heading compact">
        <p className="eyebrow">Tool</p>
        <h1>NPS / VRS</h1>
        <p>A simple patient-reported pain intensity scale from 0 to 10.</p>
      </div>

      <div className="tool-card">
        <label className="slider-label" htmlFor="pain-score">
          Pain score <strong>{pain}</strong>
        </label>
        <input
          id="pain-score"
          type="range"
          min="0"
          max="10"
          step="1"
          value={pain}
          onChange={(event) => updateState({ pain: Number(event.currentTarget.value) })}
        />
        <p className="score-output">{painBand(pain)}</p>
      </div>

      <table className="reference-table">
        <thead>
          <tr>
            <th>Score</th>
            <th>Rating</th>
          </tr>
        </thead>
        <tbody>
          {painScaleRows.map((row) => (
            <tr key={row.range}>
              <td>{row.range}</td>
              <td>{row.label}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <ReturnToPathway navigate={navigate} state={state} />
    </section>
  );
}

function FunctionTool({
  state,
  updateState,
  navigate,
}: {
  state: WorkflowState;
  updateState: (patch: NavPatch) => void;
  navigate: (path: string, patch?: NavPatch) => void;
}) {
  const hasLimitation = state.flags.includes("functionalLimitation");
  return (
    <section className="stack">
      <div className="page-heading compact">
        <p className="eyebrow">Tool</p>
        <h1>Functional assessment</h1>
        <p>Assess deep breathing, coughing, getting in/out of bed, and mobilising.</p>
      </div>

      <div className="assessment-list">
        {functionalAssessment.map((item) => (
          <article key={item.grade} className="assessment-card">
            <strong>{item.grade}: {item.label}</strong>
            <p>{item.description}</p>
          </article>
        ))}
      </div>

      <label className="check-option prominent">
        <input
          type="checkbox"
          checked={hasLimitation}
          onChange={(event) => {
            const nextFlags = event.currentTarget.checked
              ? [...state.flags, "functionalLimitation" as WorkflowFlag]
              : state.flags.filter((flag) => flag !== "functionalLimitation");
            updateState({ flags: nextFlags });
          }}
        />
        <span>Mark significant functional limitation in patient details</span>
      </label>

      <ReturnToPathway navigate={navigate} state={state} />
    </section>
  );
}

function CpotTool({
  state,
  updateState,
  navigate,
}: {
  state: WorkflowState;
  updateState: (patch: NavPatch) => void;
  navigate: (path: string, patch?: NavPatch) => void;
}) {
  const [scores, setScores] = useState<Record<string, number>>({});
  const total = Object.values(scores).reduce((sum, score) => sum + score, 0);

  useEffect(() => {
    if (Object.keys(scores).length > 0) {
      updateState({ cpot: total });
    }
  }, [scores, total, updateState]);

  const patientGroup = state.sedation === "sedated" ? "intubated" : "extubated";
  const indicators = cpotIndicators.filter((indicator) => indicator.patientGroup === "all" || indicator.patientGroup === patientGroup);

  return (
    <section className="stack">
      <div className="page-heading compact">
        <p className="eyebrow">Tool</p>
        <h1>CPOT</h1>
        <p>Critical-Care Pain Observation Tool. Total is scored out of 8.</p>
      </div>

      <div className="score-banner">
        <span>Total</span>
        <strong>{total}/8</strong>
        <small>{total > 2 ? "Above target threshold" : "At or below target threshold"}</small>
      </div>

      <div className="cpot-list">
        {indicators.map((indicator) => (
          <CpotIndicatorControl
            key={indicator.id}
            indicator={indicator}
            value={scores[indicator.id]}
            onChange={(score) => setScores((current) => ({ ...current, [indicator.id]: score }))}
          />
        ))}
      </div>

      <ReturnToPathway navigate={navigate} state={state} />
    </section>
  );
}

function CpotIndicatorControl({
  indicator,
  value,
  onChange,
}: {
  indicator: CpotIndicator;
  value: number | undefined;
  onChange: (score: number) => void;
}) {
  return (
    <fieldset className="cpot-card">
      <legend>{indicator.label}</legend>
      {indicator.options.map((option) => (
        <label key={option.label} className={value === option.score ? "radio-option selected" : "radio-option"}>
          <input
            type="radio"
            name={indicator.id}
            value={option.score}
            checked={value === option.score}
            onChange={() => onChange(option.score)}
          />
          <span>
            <strong>{option.score}: {option.label}</strong>
            <small>{option.description}</small>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

function ReturnToPathway({ navigate, state }: { navigate: (path: string, patch?: NavPatch) => void; state: WorkflowState }) {
  return (
    <div className="action-row">
      <button className="icon-text-button primary" onClick={() => navigate(pathForStage(state.stage), state)}>
        <ArrowRight size={18} aria-hidden />
        <span>Return to pathway</span>
      </button>
    </div>
  );
}

function Fieldset({ legend, children }: { legend: string; children: React.ReactNode }) {
  return (
    <fieldset className="field-group">
      <legend>{legend}</legend>
      {children}
    </fieldset>
  );
}

function SegmentedControl({
  value,
  options,
  onChange,
}: {
  value: string;
  options: Array<[string, string]>;
  onChange: (value: string) => void;
}) {
  return (
    <div className="segmented-control">
      {options.map(([optionValue, label]) => (
        <button key={optionValue} className={value === optionValue ? "selected" : ""} onClick={() => onChange(optionValue)} type="button">
          {label}
        </button>
      ))}
    </div>
  );
}

function strengthLabel(strength: RecommendationStrength) {
  if (strength === "recommended") return "Recommended";
  if (strength === "consider") return "To be considered";
  if (strength === "caution") return "Use with caution";
  return "Escalation";
}

function pathForStage(stage: WorkflowState["stage"]) {
  if (stage === "unknown") {
    return "/before-surgery";
  }

  const route = workflowRoutes.find((item) => item.id === stage);
  return route?.path ?? "/before-surgery";
}
